/* eslint-disable */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of, Subject, throwError } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'
import { Router } from '@angular/router'

import { JwtService } from '@app/modules/auth/jwt.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { LogoutResponse } from '@app/modules/auth/auth-response.model'
import { ApiService } from '@shared/services/api.service'

import camelCase from 'lodash-es/camelCase'
import isObject from 'lodash-es/isObject'
import isPlainObject from 'lodash-es/isPlainObject'
import isArray from 'lodash-es/isArray'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false

  private tokenRefreshed = new Subject<void>()
  private readonly tokenRefreshed$ = this.tokenRefreshed.asObservable()

  constructor(
    private jwtService: JwtService,
    private authService: AuthService,
    private router: Router,
    private readonly apiService: ApiService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    request = this.addAuthHeader(request)

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (event.body && event.body.data) {
            event.body.data = this.objectToCamelCase(event.body.data)
          } else if (event.body && typeof event.body !== 'string') {
            event.body.data = this.objectToCamelCase(event.body)
          }
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          // Never let the auth endpoints themselves re-enter the refresh branch. A 401 from
          // /token/refresh (expired refresh token) must propagate so the outer catchError logs out,
          // otherwise refreshToken() re-triggers while refreshTokenInProgress is true and hangs
          // forever waiting on tokenRefreshed$ (blank page, no redirect).
          const url = error.url || ''
          const isAuthEndpoint = url.includes('logout') || url.includes('token/refresh')
          if (error.status === 401 && !isAuthEndpoint && this.jwtService.getToken()) {
            if (this.jwtService.getRefreshToken()) {
              return this.refreshToken().pipe(
                switchMap(() => {
                  request = this.addAuthHeader(request)
                  return next.handle(request)
                }),
                catchError(() => {
                  this.logout()
                  return of()
                }),
              )
            } else {
              this.logout()
            }
          }

          if (error.status === 500) {
            this.router.navigate(['error-page'])
          }
        }

        return throwError(error)
      }),
    )
  }

  logout() {
    // Snapshot the token we are logging out. The server /logout call is best-effort — purge and
    // redirect must happen even if it errors (an expired token can make /logout 401 too).
    const tokenAtLogout = this.jwtService.getToken()

    const finish = (response?: LogoutResponse) => {
      const currentToken = this.jwtService.getToken()
      if (currentToken && currentToken !== tokenAtLogout) {
        // A new token was stored (user re-authenticated / impersonation) before this logout
        // completed — do not disrupt the new session.
        return
      }
      this.authService.purgeAuth()
      response?.redirectUrl ? window.open(response.redirectUrl, '_self') : void this.router.navigate(['login'])
    }

    this.authService.logout().subscribe({
      next: (response) => finish(response),
      error: () => finish(),
    })
  }

  addAuthHeader(request: HttpRequest<any>) {
    const isApiUrl = request.url.startsWith(this.apiService.baseUrl) || request.url.startsWith(this.apiService.goBaseUrl)
    if (isApiUrl && this.jwtService.getToken()) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.jwtService.getToken()}`,
        },
      })
    }
    return request
  }

  refreshToken() {
    if (this.refreshTokenInProgress) {
      return new Observable((observer) => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(null)
          observer.complete()
        })
      })
    } else {
      this.refreshTokenInProgress = true

      return this.authService.refreshToken(this.jwtService.getRefreshToken()).pipe(
        tap(() => {
          this.refreshTokenInProgress = false
          this.tokenRefreshed.next()
        }),
        catchError((error) => {
          // Clear the flag so a failed refresh doesn't poison later 401s in the session.
          this.refreshTokenInProgress = false
          return throwError(error)
        }),
      )
    }
  }

  private objectToCamelCase(data: any) {
    if (isArray(data)) {
      return data.map((object) => this.objectToCamelCase(object))
    }

    if (isPlainObject(data)) {
      const result = {}

      Object.keys(data).forEach((key) => {
        const value = data[key]
        result[camelCase(key)] = isObject(value) ? this.objectToCamelCase(value) : value
      })

      return result
    }

    return data
  }
}
